import React, { useCallback, useContext } from "react";
import URI from "urijs";
import {
  getToastRoute,
  parseLangFromRelayParam,
  parseSearchParams,
  sendToFromIfExists,
} from ".";
import {
  PrescriptionDrug,
  CoverageInfo,
  BeneficiaryAndApiHeaders,
  Beneficiary,
  IdParts,
  OecStatus,
  UserLanguage,
  AppState,
  Action,
  ActionType,
  MctToastType,
  PDE,
  MctYearPartFlag,
  OecStatuses,
  BeneInfoOtherInsuranceStatusFilter,
  MedigapCoverage,
  ThirdPartyIntegrations,
} from "../@types";
import {
  getCSRBeneInfo,
  getBeneOecStatus,
  getBeneDrugs,
  getBenePharmacies,
  getCounty,
  getCounties,
  getBeneMedigapCoverages,
} from "../api";
import { ApiError, logError } from "./errors";
import { drugsAreDuplicates, uppercaseFirst } from "./prescriptionHelpers";
import { LDFlagSet } from "launchdarkly-js-sdk-common";
import { parseDegradedIntegrations } from "./loginHelpers";
import { fetchCurrentPlan, fetchPlanIdsForNextYear } from "./planHelpers";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import routes from "../app/routes";
import {
  getYearPartInfo,
  useCurrentPlanYear,
  useYearPart,
} from "./yearFlagHelpers";
import { getPharmacyType } from "./pharmacyHelpers";
import { createNewToMedicareAlertInfo } from "./medicareInfoHelpers";
import { AppContext } from "../app/store";
import { useFlags } from "launchdarkly-react-client-sdk";

export async function getBeneInfoForCSR(
  code: string
): Promise<BeneficiaryAndApiHeaders> {
  return await getCSRBeneInfo(code);
}

export function getSLSCode(search: string): string {
  const { code } = parseSearchParams(URI.parseQuery(search));
  return code;
}

export const doesNotInclude = (
  acc: PrescriptionDrug[],
  { ndc, name }: { ndc: string; name: string }
): boolean => {
  return acc.every(
    ({ ndc: currentNDC, name: currentName }) =>
      currentNDC !== ndc && currentName !== name
  );
};

interface PlanIdParts extends IdParts {
  contract_year: string;
}

// todo: rename this to arePlansTheSame
export const isCurrentPlan = (
  coverageInfo: CoverageInfo | undefined,
  plan: PlanIdParts
): boolean => {
  if (!coverageInfo) return false;

  return (
    coverageInfo.plan_id === plan.plan_id &&
    coverageInfo.segment_id === plan.segment_id &&
    coverageInfo.contract_id === plan.contract_id &&
    (coverageInfo.contract_year === plan.contract_year ||
      coverageInfo.coverage_year === plan.contract_year)
  );
};

export const deduplicatePrescriptions = (
  prescriptions: PrescriptionDrug[]
): PrescriptionDrug[] => {
  return prescriptions.filter(
    (a, index) =>
      prescriptions.findIndex(b => drugsAreDuplicates(a, b)) === index
  );
};

export const getBeneFirstAndLastName = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  return firstName || beneficiary.lastname
    ? `${firstName} ${uppercaseFirst(beneficiary.lastname)}`.trim()
    : t("bene_landing.name_not_available");
};

export const getBeneFirstNameAndLastInitial = (
  beneficiary: Beneficiary,
  t: (key: string) => string
): string => {
  const firstName =
    beneficiary.firstname && beneficiary.firstname.length
      ? uppercaseFirst(beneficiary.firstname[0])
      : "";
  if (firstName && beneficiary.lastname) {
    return `${firstName} ${beneficiary.lastname[0].toUpperCase()}.`.trim();
  } else if (beneficiary.lastname) {
    return uppercaseFirst(beneficiary.lastname).trim();
  } else if (firstName) {
    return firstName;
  } else {
    return t("bene_landing.name_not_available");
  }
};

export const fetchOECStatus = async (
  year: string
): Promise<OecStatus | undefined> => {
  try {
    const status = await getBeneOecStatus(year);
    return status;
  } catch (_e) {
    const e = _e as ApiError;
    logError("Failed to get beneficiary OEC status (GlobalSessionHandler)", e);
    return undefined;
  }
};

export const fetchAndSetOECStatusForYearPart = async ({
  mctYearPart,
  currentYear,
  dispatch,
  onlyYear,
}: {
  mctYearPart: MctYearPartFlag;
  currentYear: number;
  dispatch: React.Dispatch<Action>;
  /** pass a year to only fetch the results for that year */
  onlyYear?: number;
}): Promise<OecStatuses> => {
  const oecStatuses: OecStatuses = {
    currentYear: undefined,
    nextYear: undefined,
  };

  const { isOpenEnrollmentNextYearOnly, isOpenEnrollment } = getYearPartInfo({
    mctYearPart,
  });

  const nextYear = currentYear + 1;

  const onlyYearCheck = (year: number) => {
    return !onlyYear || onlyYear === year;
  };

  if (!isOpenEnrollmentNextYearOnly && onlyYearCheck(currentYear)) {
    oecStatuses.currentYear = await fetchOECStatus(`${currentYear}`);
  }
  if (
    (isOpenEnrollmentNextYearOnly || isOpenEnrollment) &&
    onlyYearCheck(nextYear)
  ) {
    oecStatuses.nextYear = await fetchOECStatus(`${nextYear}`);
  }

  dispatch({
    type: ActionType.UPDATE_OEC_STATUSES,
    payload: oecStatuses,
  });
  return oecStatuses;
};

export const useOECStatuses = () => {
  const { yearPart } = useYearPart();
  const currentPlanYear = useCurrentPlanYear();
  const { state, dispatch } = useContext(AppContext);
  const { oecStatuses } = state;
  const fetchAndSetOECStatuses = useCallback(
    (
      /** the only year to get OEC statuses for */
      onlyYear?: number
    ) => {
      return fetchAndSetOECStatusForYearPart({
        mctYearPart: yearPart,
        currentYear: currentPlanYear,
        dispatch,
        onlyYear,
      });
    },
    [currentPlanYear, dispatch, yearPart]
  );
  return {
    /** fetches OECStatuses and updates the app state
     * @example fetchAndSetOECStatuses()
     * @example fetchAndSetOECStatuses(2024) // will only get 2024's statuses
     */
    fetchAndSetOECStatuses,
    oecStatuses,
  };
};

export const fetchAndSetMedigapCoverages = async ({
  status,
  dispatch,
}: {
  status?: BeneInfoOtherInsuranceStatusFilter;
  dispatch: React.Dispatch<Action>;
}): Promise<MedigapCoverage[]> => {
  const medigapCoverages = await getBeneMedigapCoverages(
    status ? status : undefined
  );
  dispatch({
    type: ActionType.UPDATE_MEDIGAP_COVERAGES,
    payload: medigapCoverages,
  });
  return medigapCoverages;
};

/**
 * Set or update beneficiary, LIS, and future LIS in app state (store)
 */
export const updateBeneInfo = ({
  beneInfo,
  dispatch,
  tmpFeEnableSummaryV2,
  tmpFeEnableNewToMedicare,
}: {
  beneInfo?: Beneficiary;
  dispatch: React.Dispatch<Action>;
  tmpFeEnableSummaryV2: boolean;
  tmpFeEnableNewToMedicare: boolean;
}) => {
  const fips = beneInfo?.mailing_address.fips_state_county;

  dispatch({
    type: ActionType.ADD_BENEFICIARY,
    payload: beneInfo,
  });

  if (tmpFeEnableSummaryV2 || tmpFeEnableNewToMedicare) {
    fetchAndSetMedigapCoverages({ dispatch }).then(medigapCoverages => {
      tmpFeEnableNewToMedicare &&
        dispatch({
          type: ActionType.UPDATE_NEW_TO_MEDICARE_ALERT_INFO,
          payload: beneInfo
            ? createNewToMedicareAlertInfo({
                currentDate: new Date(),
                bene: beneInfo,
                medigapCoverages,
              })
            : undefined,
        });
    });
  }

  dispatch({
    type: ActionType.UPDATE_LIS,
    payload: beneInfo?.lis_level,
  });

  dispatch({
    type: ActionType.UPDATE_FUTURE_LIS,
    payload: beneInfo?.future_lis_level,
  });

  // By default, use bene's mailing address to set root-level `fips` and `zipcode`
  // in `AppState`. Keeps LI bene from being prompted for these in various user-flows
  if (beneInfo?.mailing_address.zipcode) {
    dispatch({
      type: ActionType.UPDATE_ZIPCODE,
      payload: beneInfo.mailing_address.zipcode,
    });

    if (fips) {
      getCounty(fips).then(county => {
        dispatch({
          type: ActionType.UPDATE_FIPS,
          payload: fips,
        });

        dispatch({ type: ActionType.UPDATE_COUNTY, payload: county });
      });
    } else {
      // fetch fips, set it if only one
      getCounties(beneInfo.mailing_address.zipcode).then(counties => {
        if (counties.length == 1) {
          dispatch({
            type: ActionType.UPDATE_FIPS,
            payload: counties[0].fips,
          });
          dispatch({
            type: ActionType.UPDATE_COUNTY,
            payload: counties[0],
          });
        }
      });
    }
  }
};

export const useUpdateBeneInfo = () => {
  const { dispatch } = useContext(AppContext);
  const { tmpFeEnableNewToMedicare, tmpFeEnableSummaryV2 } = useFlags() as {
    tmpFeEnableNewToMedicare: boolean;
    tmpFeEnableSummaryV2: boolean;
  };

  const updateBene = useCallback(
    (beneInfo?: Beneficiary) => {
      updateBeneInfo({
        beneInfo,
        dispatch,
        tmpFeEnableSummaryV2,
        tmpFeEnableNewToMedicare,
      });
    },
    [dispatch, tmpFeEnableNewToMedicare, tmpFeEnableSummaryV2]
  );
  return updateBene;
};

export const beneKeysMatch = (bene: Beneficiary, bene2: Beneficiary) =>
  bene.meta_data.beneficiary_key === bene2.meta_data.beneficiary_key;

export interface BeneLoginAndUpdateParams {
  state: AppState;
  dispatch: React.Dispatch<Action>;
  location: RouteComponentProps["location"];
  history: RouteComponentProps["history"];
  flags: LDFlagSet;
  beneInfoWithHeaders: BeneficiaryAndApiHeaders;
}

/**
 * To be called on login or on page load fo authenticated users if the store may
 * need to be refreshed.
 *
 * `beneInfoWithHeaders` param indicates user already has global session
 */
export const beneLoginAndUpdateHandler = async ({
  state,
  dispatch,
  location,
  history,
  flags,
  beneInfoWithHeaders,
}: BeneLoginAndUpdateParams): Promise<void> => {
  const { beneficiary: stateBene, mbpHandoffFailed } = state;
  // The `relay` query param is provided on the sls-callback route.
  // If available, it is used to update user language
  const { relay } = parseSearchParams(URI.parseQuery(location.search));
  const userLang = parseLangFromRelayParam(relay) || UserLanguage.ENGLISH;
  const handlerName = "GlobalSessionHandler";
  const isLoginCallbackRoute = location.pathname === routes.slsCallback;
  const isLandingPage = location.pathname === "/";
  const { beneficiary: fetchedBene } = beneInfoWithHeaders;
  // If there's no bene info in app state at this point, or if there's both cached
  // and fetched bene info, but their keys don't match, the store will need to be refreshed
  const needsUserRefresh =
    (fetchedBene && !stateBene) ||
    (fetchedBene && stateBene && !beneKeysMatch(stateBene, fetchedBene));
  const hadMbpHandoffFailure = mbpHandoffFailed;
  const { isOutsideOpenEnrollment, isOpenEnrollmentNextYearOnly } =
    getYearPartInfo(flags);
  const { tmpFeEnableNewToMedicare, tmpFeEnableSummaryV2 } = flags;

  const authLandingRoute = routes.summary.landingPage;

  dispatch({ type: ActionType.UPDATE_LANGUAGE, payload: userLang });

  if (isLoginCallbackRoute || needsUserRefresh) {
    // Clear out any previous bene info
    dispatch({
      type: ActionType.RESET_STATE,
    });

    // Persist MBP handoff failure status, after the rest of state is reset
    if (hadMbpHandoffFailure) {
      dispatch({
        type: ActionType.UPDATE_MBP_HANDOFF_FAILED,
        payload: true,
      });
    }

    try {
      const { beneficiary: beneInfo, headers } = beneInfoWithHeaders;

      const degradedIntegrations = parseDegradedIntegrations(headers);

      if (degradedIntegrations.length) {
        dispatch({
          type: ActionType.UPDATE_DEGRADED_INTEGRATIONS,
          payload: degradedIntegrations,
        });
      }

      updateBeneInfo({
        beneInfo,
        dispatch,
        tmpFeEnableNewToMedicare,
        tmpFeEnableSummaryV2,
      });

      try {
        const drugs = await getBeneDrugs();
        dispatch({
          type: ActionType.SET_PRESCRIPTIONS,
          payload: drugs,
        });
      } catch (e) {
        logError(
          `Failed to get beneficiary drugs (${handlerName})`,
          e as ApiError
        );
      }

      try {
        const { pharmacies, mail_order } = await getBenePharmacies();
        dispatch({
          type: ActionType.SET_PHARMACIES,
          payload: pharmacies,
        });

        dispatch({
          type: ActionType.UPDATE_PHARMACY_TYPE,
          payload: getPharmacyType(pharmacies, mail_order),
        });
      } catch (e) {
        logError(
          `Failed to get beneficiary pharmacies (${handlerName})`,
          e as ApiError
        );
      }

      if (beneInfo?.current_coverage.length) {
        let nextYearPlanIds;
        if (!isOutsideOpenEnrollment || isOpenEnrollmentNextYearOnly) {
          nextYearPlanIds = await fetchPlanIdsForNextYear(
            beneInfo.current_coverage[0],
            handlerName
          );
        }
        await fetchCurrentPlan(
          beneInfo,
          state.pharmacies,
          beneInfo.lis_level,
          dispatch,
          nextYearPlanIds,
          beneInfo.future_lis_level
        );
      }

      // @TODO - figure out which other logic, if any, needs to run only on login callback route
      // or on any route other than login callback
      // `isLoginCallbackRoute` added here, since other routes should return to landing
      if (needsUserRefresh) {
        if (isLandingPage) {
          console.debug(
            `Returning from beneLoginAndUpdateHandler on navigation to main landing page, needsUserRefresh, bene has been updated in state`
          );
          return history.replace(`/?lang=${userLang}`);
        } else {
          console.debug(
            `Returning from beneLoginAndUpdateHandler, bene has been updated in state, routing to bene landing`
          );
          return history.replace(`${authLandingRoute}?lang=${userLang}`);
        }
      } else {
        console.debug(
          `Returning from beneLoginAndUpdateHandler, bene has been updated in state, routing to bene landing`
        );
        return history.replace(`${authLandingRoute}?lang=${userLang}`);
      }
    } catch (e) {
      logError(
        `Failed to retrieve beneficiary (${handlerName})`,
        e as ApiError
      );

      // @TODO - If we've gotten here, there was a "sid" but no beneficiary in app state,
      // so why are we trying to send the user to `history.location.state.from`?
      // Also, does this only make sense on login callback?
      if (needsUserRefresh) {
        return sendToFromIfExists(history);
      } else {
        // @TODO - Should this re-throw to allow the GlobalSessionHandler to actively
        // logout?
        return history.replace(
          getToastRoute(
            `${routes.questionRouting}?lang=${userLang}`,
            isLoginCallbackRoute
              ? MctToastType.BENE_FAILED_LOGIN
              : MctToastType.SESSION_EXPIRED
          )
        );
      }
    }
  } else if (stateBene && fetchedBene) {
    console.debug(
      `Returning from beneLoginAndUpdateHandler, bene already in app state`
    );
    return history.replace(`${authLandingRoute}?lang=${userLang}`);
  } else {
    console.debug(
      `Returning from beneLoginAndUpdateHandler, failed to retrieve or update bene info, or failed login`
    );
    // @TODO - Should this re-throw to allow the GlobalSessionHandler to actively
    // logout?
    return history.replace(
      getToastRoute(
        `${routes.questionRouting}?lang=${userLang}`,
        isLoginCallbackRoute
          ? MctToastType.BENE_FAILED_LOGIN
          : MctToastType.SESSION_EXPIRED
      )
    );
  }
};

export const useBeneLoginAndUpdateHandler = () => {
  const { state, dispatch } = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const flags = useFlags();
  const beneHandler = useCallback(
    (beneInfoWithHeaders: BeneficiaryAndApiHeaders) => {
      return beneLoginAndUpdateHandler({
        state,
        dispatch,
        location,
        history,
        flags,
        beneInfoWithHeaders,
      });
    },
    [dispatch, flags, history, location, state]
  );
  return beneHandler;
};

/**
 * Calculates from midnight-to-midnight
 */
export const isOverThirtyDaysSinceLogin = (
  beneficiary: Beneficiary
): boolean => {
  const {
    meta_data: { last_login_date },
  } = beneficiary;
  if (null === last_login_date || !last_login_date) {
    return true;
  }
  const today = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  const lastLoginDate = new Date(last_login_date).getTime();
  const daysSinceLastLogin = (today - lastLoginDate) / (1000 * 60 * 60 * 24);
  return daysSinceLastLogin > 30;
};

export const getPDEInfo = (
  prescriptions: PrescriptionDrug[],
  pdes?: PDE[]
): { savedPdes: PrescriptionDrug[]; unsavedPdes: PrescriptionDrug[] } => {
  const savedPdes: PrescriptionDrug[] = [];
  const unsavedPdes: PrescriptionDrug[] = [];

  if (pdes) {
    pdes.forEach(p => {
      if (!prescriptions.find(d => d.ndc === p.drug.ndc)) {
        unsavedPdes.push(p.drug);
      } else {
        savedPdes.push(p.drug);
      }
    });
  }

  return { savedPdes, unsavedPdes };
};

/**
 * Checks values passed in api response headers to determine whether BEDAP is or
 * is not healthy
 */
export const getIsBEDAPDown = (degradedIntegrations: string[]) =>
  degradedIntegrations.includes(ThirdPartyIntegrations.BEDAP);

export const useIsBEDAPDown = () => {
  const {
    state: { degradedIntegrations },
  } = useContext(AppContext);
  return getIsBEDAPDown(degradedIntegrations);
};
